import React, { FC, useEffect, useMemo } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import JournalDay from './components/JournalDay'
import {
  getFlattenedJournalEatableRecordsContainers,
  getFlattenedMenuPlanContainers,
  journalEatableRecordContainersForDate,
  menuPlanContainersForDate
} from './utils/journalEatableCardContainersForDate'
import sortJournalRecords from './utils/sortJournalRecords'

import { CockpitProps, PageLayout } from 'Components'
import {
  JournalEnergyMotionRecord,
  JournalEnergyRecord,
  JournalMotionRecord,
  Nutritions,
  QuantifiedEatable,
  ReduxStoreState,
  UserSettings
} from 'Models'
import { currentWeightSelector, fetchBodyMeasurementsAction } from 'ReduxStore/bodyMeasurements'
import { isLoadingSelector } from 'ReduxStore/common'
import { journalDayCardsListLoadingSelector } from 'ReduxStore/common/combinedSelectors'
import { fetchUserGoalNutritionsAction, userGoalNutritionsCurrentDateSelector } from 'ReduxStore/coreBackendUser'
import {
  fetchFitnessPlanAction,
  fetchFitnessPlanTemplatesAction,
  fitnessPlanSlotsForDateSelector,
  fitnessPlansForDateSelector
} from 'ReduxStore/fitnessPlan'
import { useAppDispatch, useAppSelector } from 'ReduxStore/hooks'
import { currentJournalDateSelector } from 'ReduxStore/journalDay'
import { dayJournalEatableRecordsSelector, fetchJournalEatableRecordsAction } from 'ReduxStore/journalEatableRecords'
import {
  dayJournalEnergyMotionRecordsSelector,
  fetchJournalEnergyMotionRecordsAction,
  journalEnergyMotionRecordsDataSelector
} from 'ReduxStore/journalEnergyMotionRecords'
import {
  dayJournalEnergyRecordsSelector,
  fetchJournalEnergyRecordsAction,
  journalEnergyRecordsDataSelector
} from 'ReduxStore/journalEnergyRecords'
import { fetchJournalHydrationGoal, fetchJournalHydrationRecords } from 'ReduxStore/journalHydrationRecordsSlice'
import {
  dayJournalMotionRecordsSelector,
  fetchJournalMotionRecordsAction,
  journalMotionRecordsDataSelector
} from 'ReduxStore/journalMotionRecords'
import { fetchMenuPlanAction, menuPlanNewDaysSelector } from 'ReduxStore/menuPlan'
import { denyAccessPaymentRedirectPageAfterProlongingMembership } from 'ReduxStore/temporaryAccessSlice'
import { updateUserSettingsAction, userSettingsDataSelector } from 'ReduxStore/userSettings'
import { Calculator } from 'Utils'

type SelectorProps = {
  journalEnergyMotionRecords: JournalEnergyMotionRecord[]
  journalEnergyRecords: JournalEnergyRecord[]
  journalMotionRecords: JournalMotionRecord[]
  dayJournalEnergyMotionRecords: JournalEnergyMotionRecord[]
  dayJournalEnergyRecords: JournalEnergyRecord[]
  dayJournalMotionRecords: JournalMotionRecord[]
  date: Date
  weight: number
  isUserLoading: boolean
  userSettings: UserSettings
  goalNutritions: Nutritions
  isJournalCardsListLoading: boolean
}

const mapStateToProps = createStructuredSelector<ReduxStoreState, SelectorProps>({
  journalEnergyMotionRecords: journalEnergyMotionRecordsDataSelector,
  journalEnergyRecords: journalEnergyRecordsDataSelector,
  journalMotionRecords: journalMotionRecordsDataSelector,
  dayJournalEnergyMotionRecords: dayJournalEnergyMotionRecordsSelector,
  dayJournalEnergyRecords: dayJournalEnergyRecordsSelector,
  dayJournalMotionRecords: dayJournalMotionRecordsSelector,
  date: currentJournalDateSelector,
  weight: currentWeightSelector,
  isUserLoading: (state) => isLoadingSelector(state.coreBackendUser),
  userSettings: userSettingsDataSelector,
  goalNutritions: userGoalNutritionsCurrentDateSelector,
  isJournalCardsListLoading: journalDayCardsListLoadingSelector
})

const mapDispatchToProps = {
  fetchJournalEatableRecords: fetchJournalEatableRecordsAction,
  fetchJournalMotionRecords: fetchJournalMotionRecordsAction,
  fetchJournalEnergyMotionRecords: fetchJournalEnergyMotionRecordsAction,
  fetchJournalEnergyRecords: fetchJournalEnergyRecordsAction,
  fetchBodyMeasurements: fetchBodyMeasurementsAction,
  updateUserSettings: updateUserSettingsAction,
  fetchUserGoalNutritions: fetchUserGoalNutritionsAction
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

const JournalPage: FC<PropsFromRedux> = ({
  fetchJournalEatableRecords,
  fetchJournalEnergyMotionRecords,
  fetchJournalEnergyRecords,
  fetchJournalMotionRecords,
  date,
  weight,
  dayJournalEnergyMotionRecords,
  dayJournalEnergyRecords,
  dayJournalMotionRecords,
  fetchBodyMeasurements,
  fetchUserGoalNutritions,
  goalNutritions: { energy: userGoalEnergy = 0 },
  goalNutritions,
  userSettings: { activityCompensationPercentage },
  isUserLoading,
  isJournalCardsListLoading
}: PropsFromRedux) => {
  const dispatch = useAppDispatch()
  const currentJournalDate = useAppSelector(currentJournalDateSelector)
  const menuPlanDays = useAppSelector(menuPlanNewDaysSelector)
  const fitnessPlansForDate = useAppSelector(fitnessPlansForDateSelector(currentJournalDate))
  const fitnessPlanDay = useAppSelector(fitnessPlanSlotsForDateSelector(currentJournalDate))

  const dayJournalEatableRecords = sortJournalRecords(useAppSelector(dayJournalEatableRecordsSelector))

  const menuPlanContainers = menuPlanContainersForDate({
    menuPlanDays: menuPlanDays,
    journalEatableRecordsForDate: dayJournalEatableRecords,
    date: currentJournalDate
  })

  const journalEatableRecordContainers = journalEatableRecordContainersForDate({
    journalEatableRecordsForDate: dayJournalEatableRecords
  })

  const flattenedJournalEatableRecordsContainers = getFlattenedJournalEatableRecordsContainers({
    journalEatableRecordContainers: journalEatableRecordContainers
  })

  useEffect(() => {
    fetchBodyMeasurements()
  }, [])

  useEffect(() => {
    fetchUserGoalNutritions(date)
    fetchJournalEatableRecords(date)
    fetchJournalEnergyMotionRecords(date)
    dispatch(fetchMenuPlanAction({ date: currentJournalDate, includeWeek: false }))
    dispatch(fetchFitnessPlanAction({ date: currentJournalDate }))
    dispatch(fetchFitnessPlanTemplatesAction())
    fetchJournalEnergyRecords(date)
    fetchJournalMotionRecords(date)
    dispatch(fetchJournalHydrationGoal(date))
    dispatch(fetchJournalHydrationRecords(date))
  }, [date.toISOString()])

  useEffect(() => {
    dispatch(denyAccessPaymentRedirectPageAfterProlongingMembership())
  })

  const acceptedEnergyMotionSum = (): number => {
    const acceptedMotionEnergy = Calculator.sumEnergy(
      dayJournalMotionRecords.map((jmr) => ({ energy: Calculator.calculateJournalMotionRecordEnergy(jmr, weight) }))
    )
    const visibleJournalEnergyMotionRecords = dayJournalEnergyMotionRecords.filter(
      (journalEnergyMotionRecord) => !journalEnergyMotionRecord.hidden
    )
    const acceptedDirectEnergyMotionIntake = Calculator.sumEnergy(visibleJournalEnergyMotionRecords)
    return (acceptedMotionEnergy || 0) + acceptedDirectEnergyMotionIntake
  }

  const visibleJournalEatableRecords = useMemo((): QuantifiedEatable[] => {
    return flattenedJournalEatableRecordsContainers
      .filter((card) => card.journalRecordId && card.journalRecordId.length > 0)
      .map(({ eatableCardObjects }) => {
        return { ...eatableCardObjects[0].eatable }
      })
  }, [menuPlanContainers])

  const cockpitProps = useMemo(
    (): CockpitProps => ({
      isLoading: isUserLoading,
      acceptedNutritions: Calculator.sumNutritionsOfQuantfiedEatables(...visibleJournalEatableRecords),
      acceptedDirectEnergyIntake: Calculator.sumEnergy(dayJournalEnergyRecords),
      goalNutritions,
      goalEnergy: userGoalEnergy,
      acceptedEnergyMotionSum: acceptedEnergyMotionSum(),
      activityCompensationPercentage
    }),
    [
      isUserLoading,
      visibleJournalEatableRecords,
      goalNutritions,
      userGoalEnergy,
      dayJournalEnergyRecords,
      dayJournalEnergyMotionRecords,
      dayJournalMotionRecords,
      weight,
      activityCompensationPercentage
    ]
  )

  const renderedJournalDay = useMemo(() => {
    const flattenedMenuPlanContainers = getFlattenedMenuPlanContainers({ menuPlanContainers })

    const sumCards =
      dayJournalEnergyRecords.length +
      dayJournalEnergyMotionRecords.length +
      dayJournalMotionRecords.length +
      flattenedJournalEatableRecordsContainers.length +
      flattenedMenuPlanContainers.length +
      fitnessPlanDay.length

    const showJournalCardsLoading = isJournalCardsListLoading && sumCards === 0

    return (
      <>
        <JournalDay
          journalEatableRecordContainers={journalEatableRecordContainers}
          menuPlanContainers={menuPlanContainers}
          flattenedJournalEatableRecordsContainers={flattenedJournalEatableRecordsContainers}
          journalEnergyMotionRecords={dayJournalEnergyMotionRecords}
          journalEnergyRecords={dayJournalEnergyRecords}
          journalMotionRecords={dayJournalMotionRecords}
          fitnessPlansForDate={fitnessPlansForDate}
          weight={weight}
          acceptedEnergyMotionSum={acceptedEnergyMotionSum()}
          cockpitProps={cockpitProps}
          showJournalCardsLoading={showJournalCardsLoading}
        />
      </>
    )
  }, [
    flattenedJournalEatableRecordsContainers,
    dayJournalEnergyMotionRecords,
    dayJournalEnergyRecords,
    dayJournalMotionRecords,
    cockpitProps,
    journalEatableRecordContainers,
    menuPlanContainers,
    fitnessPlanDay,
    fitnessPlansForDate,
    weight,
    isJournalCardsListLoading
  ])

  return (
    <PageLayout variant="smallcircle" style={{ paddingTop: '3.5rem' }}>
      {renderedJournalDay}
    </PageLayout>
  )
}

export default connector(JournalPage)
